<template>
  <v-flex xs12 md6 px-2 pb-4>
    <v-card>
      <v-form :id="dataSetFormId" v-model="valid" ref="form" lazy-validation>
        <v-card-title :style="panelColour">
          <div class="circle mr-2" :style="dataSetGroupColour">
            <span v-if="!isFirstElement">{{dataSet.logic}}</span>
          </div>
          <span class="truncate">{{dataSet.editorDescription}}</span>
          <v-spacer/>
          <v-icon :color="panelColour.color">notifications_none</v-icon>
        </v-card-title>
        <v-divider/>
        <v-card-text>
          <v-container grid-list-xs>
            <v-layout row wrap>
              <!--Data Set-->
              <v-flex xs12 sm8>
                <v-select :id="dataSetId" :aria-label="dataSetId" v-model="dataSetSelection"
                          ref="dataSetSelection"
                          :items="dataSetSelectionItems"
                          :label="$t('admin.alerts.new.data_sets.data_set_label')"
                          required
                          :rules="requiredRule">
                </v-select>
              </v-flex>
              <!--logic-->
              <v-flex xs12 sm3 offset-sm1>
                <v-select v-if="!isFirstElement" :id="logicId" :aria-label="logicId" v-model="logicSelection"
                          ref="logicSelection"
                          :items="logicSelectionItems"
                          :label="isFirstElement ? '' : $t('admin.alerts.new.data_sets.logic_label')"
                          :disabled="isFirstElement"
                          required
                          :rules="requiredLogicRule">
                </v-select>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <!--filter type-->
              <v-flex xs12>
                <!--slider-->
                <template v-if="filterTypeSlider">
                  <span>{{$t('admin.alerts.new.data_sets.filter_type_label')}}</span>
                  <vue-slider ref="slider" :data="sliderSelectionItems" :piecewiseLabel="true"
                              tooltip="hover" :tooltip-dir="'top'" :processStyle="sliderStyle"
                              :tooltipStyle="tooltipStyle" v-model="sliderSelection" :lazy="true" :show="true" class="pb-4">
                    <div slot="label" slot-scope="state" :class="[sliderCircle(state)]">
                      {{sliderCircleValue(state)}}
                    </div>
                  </vue-slider>
                </template>
                <!--combo-->
                <v-select :id="comboFilterId" :aria-label="comboFilterId" v-else-if="filterTypeCombo"
                          v-model="comboSelection"
                          ref="comboSelection"
                          :items="comboSelectionItems"
                          multiple hide-selected chips
                          deletable-chips
                          :label="$t('admin.alerts.new.data_sets.filter_type_label')"
                          required
                          :rules="requiredSelectionRule">
                </v-select>
              </v-flex>
            </v-layout>
            <!--threshold type-->
            <v-layout>
              <!--single-->
              <v-flex v-if="thresholdTypeSingle" xs12 sm3>
                <v-text-field :id="singleThresholdId" :aria-label="singleThresholdId" v-model="singleValue"
                              :label="$t(`admin.alerts.new.data_sets.thresholdQualifier.${dataSetItem.thresholdQualifier}`)"
                              required
                              :rules="requiredRule">
                </v-text-field>
              </v-flex>
              <!--range-->
              <template v-else-if="thresholdTypeRange">
                <v-flex xs10 sm6 class="mr-3">
                  <v-select :id="rangeThresholdOptionId" :aria-label="rangeThresholdOptionId" v-if="thresholdTypeRange"
                            v-model="rangeSelection"
                            ref="rangeSelection"
                            :items="rangeSelectionItems"
                            :label="$t('admin.alerts.new.data_sets.threshold_label')"
                            required
                            :rules="requiredRule">
                  </v-select>
                </v-flex>
                <v-flex xs2>
                  <v-text-field :id="rangeThresholdMinValueId" :aria-label="rangeThresholdMinValueId" v-model="thresholdMinValue"
                                :label="$t(`admin.alerts.new.data_sets.thresholdQualifier.${dataSetItem.thresholdQualifier}`)"
                                :suffix="thresholdKey"
                                required
                                :rules="requiredRule">
                  </v-text-field>
                </v-flex>
                <v-flex v-if="isBetweenOption" xs2>
                  <p class="text-xs-center pt-4">{{$t('admin.alerts.new.data_sets.between_and')}}</p>
                </v-flex>
                <v-flex v-if="isBetweenOption" xs2>
                  <v-text-field :id="rangeThresholdMaxValueId" :aria-label="rangeThresholdMaxValueId" v-model="thresholdMaxValue"
                                :label="$t(`admin.alerts.new.data_sets.thresholdQualifier.${dataSetItem.thresholdQualifier}`)"
                                :suffix="thresholdKey"
                                required
                                :rules="requiredRule">
                  </v-text-field>
                </v-flex>
              </template>
            </v-layout>
            <!--duration-->
            <v-layout v-if="dataSetSelection">
              <v-flex xs12 sm3>
                <v-text-field :id="durationId" :aria-label="durationId" v-model="durationValue"
                              :label="$t('admin.alerts.new.data_sets.duration_label')"
                              required
                              :rules="durationRule"
                :mask="numberMask">
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-tooltip top>
            <v-btn :id="dataSetCloseId" :aria-label="dataSetCloseId" slot="activator" icon @click="clickClose">
              <v-icon v-if="dataSet.submitted">visibility</v-icon>
              <v-icon v-else>delete</v-icon>
            </v-btn>
            <span>{{closeTooltip}}</span>
          </v-tooltip>
          <v-spacer/>
          <v-tooltip top>
            <v-btn :id="dataSetSubmitId" :aria-label="dataSetSubmitId" slot="activator" icon @click="clickSubmit">
              <v-icon>check</v-icon>
            </v-btn>
            <span>{{$t('admin.alerts.new.data_sets.submit')}}</span>
          </v-tooltip>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-flex>
</template>
<script>
  import {
    RESET_DATA_SET_VALUES,
    SET_DATA_SET_ITEM_VISIBILITY,
    UNDO_CREATE_NEW_DATA_SET_ITEM,
    UPDATE_DATA_SET_PROP
  } from '../../../store/mutation-types'
  import vueSlider from 'vue-slider-component'
  import TooltipStyle from '../../../mixins/TooltipStyle'
  import { getDataSetDescription } from '../../../HelperMethods'
  import { mapMutations } from 'vuex'
  import Helpers from '../../../mixins/Helpers'
  import Colors from '../../../mixins/Colors'
  import { POST_NEW_DATA_SET_ITEM, UPDATE_DATA_SET_ITEM } from '../../../store/action-types'

  export default {
    name: 'alert-dataset-editor-item',
    components: {vueSlider},
    mixins: [TooltipStyle, Helpers, Colors],
    data () {
      return {
        dataSetSelectionItems: [],
        logicSelectionItems: [{
          value: 'AND',
          text: 'And'
        }, {
          value: 'OR',
          text: 'Or'
        }],
        comboSelectionItems: [],
        sliderSelectionItems: [],
        rangeSelectionItems: [],
        thresholdKey: '',
        state: false,
        valid: false,
        requiredLogicRule: [v => (this.isFirstElement || !!v) || this.$t('field_required')],
        requiredRule: [v => !!v || this.$t('field_required')],
        requiredSelectionRule: [v => (!!v && v.length > 0) || this.$t('field_required')],
        durationRule: [v => !!v || this.$t('field_required'), v => v > 0 || this.$t('admin.alerts.new.data_sets.duration_rule')],
        numberMask: '###'
      }
    },
    props: {
      panelColour: {
        type: Object
      },
      alertId: {
        required: true
      },
      dataSet: {
        type: Object
      },
      dataSetKey: {
        type: Number
      },
      firstElement: {
        type: Number
      }
    },
    methods: {
      ...mapMutations({
        updateProp: UPDATE_DATA_SET_PROP
      }),
      clickClose () {
        if (this.dataSet.submitted) {
          this.$store.commit(SET_DATA_SET_ITEM_VISIBILITY, {key: this.dataSetKey, visible: !this.dataSet.visible})
        } else {
          this.$store.commit(UNDO_CREATE_NEW_DATA_SET_ITEM)
        }
      },
      clickSubmit () {
        if (this.$refs.form.validate()) {
          this.updateThisProp({prop: 'submitted', val: true})
          // update descriptions
          let key = getDataSetDescription(this.dataSet)
          let z = this.isBetweenOption ? key.z : ''
          let kk = this.isBetweenOption ? this.$t(key.k) : ''
          this.updateThisProp({
            prop: 'description',
            val: this.$t(key.text, {x: key.x, y: key.y, z: z, k: this.$t(key.k), kk: kk, d: key.d})
          })
          this.updateThisProp({
            prop: 'editorDescription',
            val: this.$t(key.text, {x: key.x, y: key.y, z: z, k: this.$t(key.k), kk: kk, d: key.d})
          })
          // submit
          if (this.dataSet.id) {
            this.$store.dispatch(UPDATE_DATA_SET_ITEM, {dataSetId: this.dataSet.id, dataSet: this.dataSet})
          } else {
            this.$store.dispatch(POST_NEW_DATA_SET_ITEM, {id: this.alertId, dataSet: this.dataSet})
          }
        }
      },
      sliderCircleValue (state) {
        return state.label.substring(0, 1)
      },
      sliderCircle (state) {
        return `slider-circle slider-circle-${this.sliderCircleValue(state)}`
      },
      buildThresholdKey (obj) {
        let key = `admin.alerts.new.data_sets.thresholdKey.${obj.thresholdKey}`
        return this.$i18n.keyExists(key) ? this.$t(key) : ''
      },
      updateThisProp (prop) {
        this.updateProp({
          key: this.dataSetKey,
          prop: prop.prop,
          val: prop.val
        })
      },
      setStarterDefaults () {
        this.dataSetSelectionItems = this.buildDataSetSelectionItems
      },
      checkUndefined (setUp) {
        if (setUp.filterValues === undefined) { setUp.filterValues = [] }
        if (setUp.thresholdConditions === undefined) { setUp.thresholdConditions = [] }
        if (setUp.thresholdKey === undefined) { setUp.thresholdKey = '' }
      },
      setDefaults (setUp) {
        this.checkUndefined(setUp)
        this.comboSelectionItems = setUp.filterValues ? setUp.filterValues : []
        this.comboSelection = ''
        this.sliderSelectionItems = setUp.filterValues ? setUp.filterValues.map(el => el.text) : []
        this.rangeSelectionItems = setUp.thresholdConditions ? setUp.thresholdConditions : []
        this.thresholdKey = setUp.thresholdKey ? this.buildThresholdKey(setUp) : ''
        this.rangeSelection = ''
        this.thresholdMinValue = ''
        this.thresholdMaxValue = ''
        this.singleValue = ''
        this.durationValue = ''
      }
    },
    computed: {
      dataSetItem () {
        return this.dataSet.dataSetItem
      },
      dataSetItems () {
        return this.$store.state.admin.dataSetSelectionItems
      },
      isFirstElement () {
        return this.dataSet.id === this.firstElement
      },
      dataSetFormId () {
        return `data-set-form-${this.dataSetKey}`
      },
      dataSetCloseId () {
        return `close-data-set-${this.dataSetKey}`
      },
      dataSetSubmitId () {
        return `submit-data-set-${this.dataSetKey}`
      },
      translateDescription () {
        let key = getDataSetDescription(this.dataSet)
        return this.$t(key.text, {x: key.x, y: key.y, z: key.z, k: this.$t(key.k), kk: this.$t(key.k), d: key.d})
      },
      dataSetGroupColour () {
        if (this.dataSet.logicColour !== null) {
          return `color: ${this.logicColours[this.dataSet.logicColour]}`
        }
      },
      selectedDataSet () {
        return this.dataSetItems.filter(el => el.dataSetIdentifier === this.dataSetSelection)[0]
      },
      filterTypeSlider () {
        if (!this.dataSetSelection) { return false }
        if (this.selectedDataSet.filterType === undefined) { return false }
        return this.selectedDataSet.filterType === 'slider'
      },
      filterTypeCombo () {
        if (!this.dataSetSelection) { return false }
        if (this.selectedDataSet.filterType === undefined) { return false }
        return this.selectedDataSet.filterType === 'combo'
      },
      thresholdTypeSingle () {
        if (!this.dataSetSelection) { return false }
        if (this.selectedDataSet.thresholdType === undefined) { return false }
        return this.selectedDataSet.thresholdType === 'single'
      },
      thresholdTypeRange () {
        if (!this.dataSetSelection) { return false }
        if (this.selectedDataSet.thresholdType === undefined) { return false }
        return this.selectedDataSet.thresholdType === 'range'
      },
      buildDataSetSelectionItems () {
        return this.translateFields(this.dataSetItems.map(el => {
          return {
            text: `admin.alerts.new.data_sets.items.${el.dataSetIdentifier}`,
            value: el.dataSetIdentifier
          }
        }))
      },
      dataSetId () {
        return `data-set-option-${this.dataSetKey}`
      },
      logicId () {
        return `logic-option-${this.dataSetKey}`
      },
      comboFilterId () {
        return `filter-type-combo-option-${this.dataSetKey}`
      },
      singleThresholdId () {
        return `threshold-type-single-${this.dataSetKey}`
      },
      rangeThresholdOptionId () {
        return `threshold-type-range-option-${this.dataSetKey}`
      },
      rangeThresholdMinValueId () {
        return `threshold-type-range-min-value-${this.dataSetKey}`
      },
      rangeThresholdMaxValueId () {
        return `threshold-type-range-max-value${this.dataSetKey}`
      },
      durationId () {
        return `duration-value-${this.dataSetKey}`
      },
      closeTooltip () {
        return this.dataSet.submitted ? this.$t('admin.alerts.new.data_sets.hide') : this.$t('admin.alerts.new.data_sets.delete')
      },
      isBetweenOption () {
        return this.rangeSelection === 'BETWEEN'
      },
      dataSetSelection: {
        get () {
          return this.dataSetItem.dataSetIdentifier
        },
        set (val) {
          let value = this.dataSetItems.filter(el => el.dataSetIdentifier === val)[0]
          if (value) {
            this.updateThisProp({prop: 'dataSetItem', val: value})

            // reset values
            this.setDefaults(value)
            this.$store.commit(RESET_DATA_SET_VALUES, this.dataSetKey)
            // reset description
            let key = getDataSetDescription(this.dataSet)
            let y = this.durationValue ? key.y : 'y'
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: 'x', y: y, yy: this.$t(key.yy), z: ''})
            })
          }
        }
      },
      logicSelection: {
        get () {
          return this.dataSet.logic
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'logic', val: val})
          }
        }
      },
      sliderSelection: {
        get () {
          return this.dataSet.minThreshold && this.dataSet.maxThreshold
            ? [this.dataSet.minThreshold, this.dataSet.maxThreshold]
            : [this.selectedDataSet.filterValues[0].text, this.selectedDataSet.filterValues[0].text]
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'minThreshold', val: val[0]})
            this.updateThisProp({prop: 'maxThreshold', val: val[1]})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let y = this.durationValue ? key.y : 'y'
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: key.x, y: y, yy: '', z: ''})
            })
          }
        }
      },
      comboSelection: {
        get () {
          return this.dataSet.filterValue
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'filterValue', val: val})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let x = this.singleValue ? key.x : 'x'
            let y = val.length > 0 ? key.y : 'y'
            let z = this.durationValue ? key.z : 'z'
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: x, y: y, yy: '', z: z})
            })
          }
        }
      },
      rangeSelection: {
        get () {
          return this.dataSet.thresholdCondition
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'thresholdCondition', val: val})
            // reset max if BETWEEN
            if (!this.isBetweenOption) {
              this.updateThisProp({prop: 'maxThreshold', val: null})
            }

            // update description
            let key = getDataSetDescription(this.dataSet)
            let y = this.thresholdMinValue ? key.y : 'y'
            let z = this.isBetweenOption ? this.thresholdMaxValue ? key.z : 'z' : ''
            let d = this.durationValue ? key.d : 'd'
            let kk = this.isBetweenOption ? this.$t(key.k) : ''
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: key.x, y: y, z: z, k: this.$t(key.k), kk: kk, d: d})
            })
          }
        }
      },
      thresholdMinValue: {
        get () {
          return this.dataSet.minThreshold
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'minThreshold', val: val})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let x = this.rangeSelection ? key.x : 'x'
            let z = this.isBetweenOption ? this.thresholdMaxValue ? key.z : 'z' : ''
            let d = this.durationValue ? key.d : 'd'
            let kk = this.isBetweenOption ? this.$t(key.k) : ''
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: x, y: key.y, z: z, k: this.$t(key.k), kk: kk, d: d})
            })
          }
        }
      },
      thresholdMaxValue: {
        get () {
          return this.dataSet.maxThreshold
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'maxThreshold', val: val})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let x = this.rangeSelection ? key.x : 'x'
            let y = this.thresholdMinValue ? key.y : 'y'
            let d = this.durationValue ? key.d : 'd'
            let kk = this.isBetweenOption ? this.$t(key.k) : ''
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: x, y: y, z: key.z, k: this.$t(key.k), kk: kk, d: d})
            })
          }
        }
      },
      singleValue: {
        get () {
          return this.dataSet.minThreshold
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'minThreshold', val: val})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let y = this.comboSelection && this.comboSelection.length > 0 ? key.y : 'y'
            let z = this.durationValue > 0 ? key.z : 'z'
            this.updateThisProp({
              prop: 'editorDescription',
              val: this.$t(key.text, {x: key.x, y: y, yy: '', z: z})
            })
          }
        }
      },
      durationValue: {
        get () {
          return this.dataSet.consecutiveDays
        },
        set (val) {
          if (val) {
            this.updateThisProp({prop: 'consecutiveDays', val: val})

            // update description
            let key = getDataSetDescription(this.dataSet)
            let final = ''
            if (this.dataSet.dataSetItem.dataSetIdentifier === 'ENGAGEMENT') {
              final = this.$t(key.text, {x: key.x, y: key.y})
            } else if (this.dataSet.dataSetItem.dataSetIdentifier === 'ATTENDANCE') {
              let z = this.isBetweenOption ? this.thresholdMaxValue ? key.z : 'z' : ''
              let kk = this.isBetweenOption ? this.$t(key.k) : ''
              final = this.$t(key.text, {x: key.x, y: key.y, z: z, k: this.$t(key.k), kk: kk, d: key.d})
            } else {
              let x = key.x ? key.x : 'x'
              let y = key.y ? key.y : 'y'
              let yy = key.yy ? key.yy : ''
              final = this.$t(key.text, {x: x, y: y, yy: this.$t(yy), z: key.z})
            }
            this.updateThisProp({prop: 'editorDescription', val: final})
          }
        }
      }
    },
    created () {
      this.setStarterDefaults()
      if (this.dataSetSelection) {
        this.setDefaults(this.selectedDataSet)
      }
    }
  }
</script>

<style scoped>
  .slider-circle {
    color: white;
    font-weight: bold;
    font-size: 12px;
    border-radius: 10px;
    padding-left: 6px;
    padding-top: 1px;
    margin: 14px 0px 0px -7px;
    width: 20px;
    height: 20px;
  }
  .slider-circle-N {
    background-color: #9E9E9E;
  }
  .slider-circle-V {
    background-color: #F44336;
  }
  .slider-circle-L {
    background-color: #FF9800;
  }
  .slider-circle-P {
    background-color: #BA68C8;
  }
  .slider-circle-G {
    background-color: #03A9F4;
  }
  .slider-circle-H {
    background-color: #4CAF50;
  }
  .circle {
    border-radius: 100%;
    width: 2.5rem;
    height: 2.5rem;
    text-align: center;
    padding-top: 7px;
    background-color: white;
  }
</style>



// WEBPACK FOOTER //
// src/components/settings/alert/AlertDatasetEditorItem.vue